import { FC, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { 
  Box, Stepper, Typography, 
  Step, StepLabel, Alert,
  useTheme,
  LinearProgress,
  Container,
  Stack,
  Divider,
  IconButton,
  Tooltip,
  ClickAwayListener
} from "@mui/material";
import useMediaQuery from '@mui/material/useMediaQuery';

import * as electricityContractationActions from "./actions/electricityContractation";
import Settings from "../../settings";
import HelpMessage from "../HelpMessage";
import useElectricitySteps from "./hooks/useElectricitySteps";
import { default as leadsService } from "./services/leads";
import { RootState } from "@/store";
import { Button, RichContent } from "@gisce/oficina-virtual-components";
import { useNavigate } from "react-router";
import { Help } from "@mui/icons-material";
import { SubmitButtonContext } from "./Contractation";

export interface ElectricityContractationProps {
  dualContractation?: boolean;
}

const ElectricityContractation: FC<ElectricityContractationProps> = ({
  dualContractation = false
}) => {
  const {values: submitValues} = useContext(SubmitButtonContext);
  const theme = useTheme();
  const breakpointUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();

  const contractation = useSelector((state: RootState) => state.electricityContractation);
  
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { steps, handleGoBack } = useElectricitySteps({dualContractation});

  const [helpTooltipOpen, setHelpTooltipOpen] = useState(false);

  const phoneLink = (Settings?.organization?.phone_crm ?? "").replace(/\s/g, '');
  const phone = Settings?.organization?.phone_crm ?? "";

  useEffect(() => {
    leadsService.fetchContractationConfig().then(res => {
      dispatch(
        electricityContractationActions.contractationConfigurationReceive(res)
      );
    })
    
    import("../../overrides/hooks/contractation").then(
      ({chatHook}: {chatHook: unknown}) => {
        if (typeof chatHook === "function" && !!chatHook()) {
          document.body.appendChild(chatHook());
        }  
      }
    );
  }, []);

  useEffect(() => {
    if ( contractation.currentStep.name == 'confirmation' ) {
      import("../../overrides/hooks/contractation").then(
        ({trackingHook}) => {
          if (typeof trackingHook === "function" && !!trackingHook()) {
            let content =  trackingHook();
            if (content) {
              content = content.replace("<insert order id value here>", contractation.cups);
              document.head.prepend(document.createRange().createContextualFragment(content));
            }
          } 
        }
      );
    }

    // eslint-disable-next-line
  }, [contractation.currentStep.index]);


  const handleHelpTooltipClose = () => {
    setHelpTooltipOpen(false);
  };

  const handleHelpTooltipOpen = () => {
    setHelpTooltipOpen(true);
  };

  return (
    <>
      {breakpointUpMd ? 
        <Box mb={2}>
          <Typography variant="h4" style={{marginBottom: 10}}>
            {dualContractation ? 
              t('common:text.contractation_dual_title') : 
              t('common:text.contractation_title')
            }
          </Typography>
          {Settings?.contractation?.helpMessage &&
            <HelpMessage
              message = {
                t('common:text.contractation_help_message', 
                  { phone: phone, phoneLink: phoneLink, })
              }
            />
          }
        </Box>
      :
        <Box 
          sx={{
            height: "90px",
            boxShadow: theme.shadows[5]
          }}
        >
          <Stack 
            direction="column" 
            alignItems="center"
            justifyContent="center"
            sx={{
              height: "100%",
              paddingLeft: 1,
              paddingRight: 1
            }}
          >

            <Stack direction="row" alignItems="center">
              <Typography variant="body1" component="div">
                {dualContractation ? 
                  t('common:text.contractation_dual_title') : 
                  t('common:text.contractation_title')
                }
              </Typography>
              
              {Settings?.contractation?.helpMessage &&
                <ClickAwayListener onClickAway={handleHelpTooltipClose}>
                  <Tooltip 
                    PopperProps={{
                      disablePortal: true,
                    }}
                    onClose={handleHelpTooltipClose}
                    open={helpTooltipOpen}
                    title={
                      <RichContent
                        content={t('common:text.contractation_help_message', 
                          { phone: phone, phoneLink: phoneLink, })
                        }
                      />
                    }
                  >
                    <IconButton onClick={handleHelpTooltipOpen}>
                      <Help />
                    </IconButton>
                  </Tooltip> 
                </ClickAwayListener>
              }

            </Stack>
            
            <Typography variant="h6">
              {contractation.currentStep.index + 1} / {steps.length} {t(steps[contractation.currentStep.index].label)}
            </Typography>

            <LinearProgress 
              variant="determinate" 
              value={
                ((contractation.currentStep.index + 1)  / (steps.length))*100
              } 
              sx={{
                width: "100%"
              }}
            />

          </Stack>
          
        </Box>
      }

      {breakpointUpMd ?
        <>
          <Stepper 
            activeStep={contractation.currentStep.index} 
            alternativeLabel
            sx={{mb: 2}}
          >

            {steps.map(({label}, idx) => <Step 
                key={idx} 
                completed={contractation.completedSteps[idx] ? true : false}
              >
                <StepLabel>
                    {t(label)}
                </StepLabel>
              </Step>
            )}

          </Stepper>

          {steps[contractation.currentStep.index].content}

          <Divider sx={{mt: 3, mb: 3}}/>
          <Stack direction="row" gap={2}>
            {(contractation.currentStep.index > 0 && 
              contractation.currentStep.index < steps.length-1 ) && 
              <Button
                onClick={handleGoBack} 
                disabled={submitValues.loading}
              >
                {t('common:text.contractation_previous')}
              </Button>
            }
            {contractation.currentStep.index < steps.length-1 && 
              <Button
                id={`contractation_${steps[contractation.currentStep.index].name}`}
                color={'primary'}
                variant={'contained'}
                {...submitValues}
              >
                {t('common:text.contractation_next')}
              </Button>
            }
            {contractation.currentStep.index == steps.length-1 && 
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  dispatch(electricityContractationActions.resetContractation());
                  navigate("/")
                }}
              >
                {t('common:text.go_home')}
              </Button>
            }
          </Stack>

          { contractation.leadErrors.genericError &&
            <Alert severity={"error"} variant="filled" sx={{mt: 2}}>
              {t("common:text.contractation_leads_generic_error")}
            </Alert>
          }

        </>
      : 
        <>
          {/* The appbar height when down md is always 64px */}
          <Container 
            sx={{
              height: `calc((100vh - 64px) - (90px + 55px))`, 
              overflow: "auto"
            }}>
            <Box mt={2}>
              {steps[contractation.currentStep.index].content}
       
              { contractation.leadErrors.genericError &&
                <Alert severity={"error"} variant="filled">
                  {t("common:text.contractation_leads_generic_error")}
                </Alert>
              }
            </Box>
          </Container>
          <Container 
            sx={{
              height: "55px",
              boxShadow: theme.shadows[11]
            }}
          >
            <Stack 
              direction="row" 
              gap={1} 
              justifyContent="space-around"
              sx={{height: "100%", pt: 1, pb: 1}}
            >
              {(contractation.currentStep.index > 0 && 
              contractation.currentStep.index < steps.length-1  ) &&
                <Button
                  onClick={handleGoBack} 
                  disabled={submitValues.loading}
                >
                  {t('common:text.contractation_previous')}
                </Button>
              }
              {contractation.currentStep.index < steps.length-1 && 
                <Button
                  id={`contractation_${steps[contractation.currentStep.index].name}`}
                  color="primary"
                  variant="contained"
                  {...submitValues}
                >
                  {t('common:text.contractation_next')}
                </Button>
              }
              {contractation.currentStep.index == steps.length-1 && 
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    dispatch(electricityContractationActions.resetContractation());
                    navigate("/")
                  }}
                >
                  {t('common:text.go_home')}
                </Button>
              }
            </Stack>
          </Container>
        </>
      }
      
    </>
  );
};

export default ElectricityContractation;
